import React, { useState } from "react"
import {
  CustomReasonCode,
  Maybe,
  OrderModificationShippingFees,
  ShippingFee
} from "lib/types/generated/graphql-types"
import { ReactComponent as ShippingIcon } from "images/icons/cart-flatbed-boxes.svg"

import dayjs from "dayjs"
import {
  AdditionalData,
  BaseData,
  BasePrice,
  Bold,
  Container,
  Dates,
  ExpandIcon,
  ImageContainer,
  Info,
  Label,
  NotCompleted,
  OrderLineWrapper,
  PaymentInfo,
  ProviderStatus
} from "./Shared.styled"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import Status from "../../Ui/Status"
import { useQuery } from "@apollo/client"
import LoadingRow from "./LoadingRow"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import Totals from "./Totals"
import Reason from "./Reason"
import GET_SHIPPING_FEE_MODIFICATION from "../../../graphql/queries/order/GetShippingFeeModification"

type Props = {
  modificationId: string
  currencyCode: string
  shippingFees: ShippingFee[]
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
}
const ModificationShippingFees = ({
  modificationId,
  shippingFees,
  currencyCode,
  getCustomCode
}: Props) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const getShippingFee = (id: string) => {
    return shippingFees?.find((fee) => fee.id === id)
  }

  const { data, loading } = useQuery(GET_SHIPPING_FEE_MODIFICATION, {
    variables: { modificationId }
  })

  const modification = data?.Order?.orderModificationShippingFees as OrderModificationShippingFees
  const modificationShippingFees = modification?.shippingFees ?? []

  if (loading) return <LoadingRow />

  return (
    <Container>
      {!modification?.completed && <NotCompleted>Not completed</NotCompleted>}
      <BaseData onClick={() => setExpanded(!expanded)}>
        <h3>Modification #{modification.id}</h3>
        <Dates>
          <div>
            Started: <b>{dayjs(modification.created).format("YYYY-MM-DD : HH:mm:ss")}</b>
          </div>
          <div>
            Last updated: <b>{dayjs(modification.updated).format("YYYY-MM-DD : HH:mm:ss")}</b>
          </div>
          {modification?.completed && (
            <div>
              Completed: <b>{dayjs(modification?.completed).format("YYYY-MM-DD : HH:mm:ss")}</b>
            </div>
          )}
          <div>
            Shipping fees: <b>{modificationShippingFees.length}</b>
          </div>
          {modification?.restarted && (
            <>
              <div>
                Restarted: <b>{dayjs(modification.restarted).format("YYYY-MM-DD : HH:mm:ss")}</b>
              </div>
              <div>
                Restarts: <b>{modification.restarts}</b>
              </div>
            </>
          )}
        </Dates>
      </BaseData>
      <Reason getCustomCode={getCustomCode} reason={modification?.reason} />
      {expanded && (
        <AdditionalData>
          {modificationShippingFees.map((shippingFee) => (
            <OrderLineWrapper key={shippingFee.data.id}>
              <Info>
                <ImageContainer>
                  <ShippingIcon />
                </ImageContainer>
                <div>
                  <Bold>{getShippingFee(shippingFee?.data?.id)?.name}</Bold>
                  <BasePrice>
                    <Money
                      amount={shippingFee?.data?.price?.basePriceAmount ?? 0}
                      currencyUnit={currencyCode as DineroFactory.Currency}
                    />
                  </BasePrice>
                  <div>
                    <Money
                      amount={shippingFee?.data?.price?.salePriceAmount ?? 0}
                      currencyUnit={currencyCode as DineroFactory.Currency}
                    />
                  </div>
                </div>
              </Info>
            </OrderLineWrapper>
          ))}
          <ProviderStatus>
            <div>
              <Label>Payment status:</Label>
              <Status status={modification?.paymentProvider?.status?.current ?? ""} />
            </div>
          </ProviderStatus>
          <PaymentInfo></PaymentInfo>
          {modification.oldTotals && (
            <Totals totals={modification.oldTotals} currencyCode={currencyCode} label="Old total" />
          )}
          {modification.newTotals && (
            <Totals totals={modification.newTotals} currencyCode={currencyCode} label="New total" />
          )}
        </AdditionalData>
      )}
      <ExpandIcon $expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <DownIcon />
      </ExpandIcon>
    </Container>
  )
}

export default ModificationShippingFees
